import { useState } from 'react';
import { CreditCard, CheckCircle, Crown, Shield, Rocket, Star, Wallet } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../api/api';

export const SubscriptionPanel = ({ userData }: { userData: any }) => {
    const { t } = useTranslation();
    const [selectedPlan, setSelectedPlan] = useState('TRADER');
    const [paymentMethod, setPaymentMethod] = useState<'CARD' | 'CRYPTO'>('CARD'); // 👈 Выбор метода
    const [loading, setLoading] = useState(false);

    // Текущий план пользователя
    const currentPlan = userData?.plan || 'FREE';
    const isFree = currentPlan === 'FREE';

    // Конфигурация тарифов
    const PLANS = [
        {
            id: 'START',
            name: t('landing.pricing.p1.title'),
            price: 29,
            period: t('landing.pricing.period'),
            features: [
                t('landing.pricing.p1.f1'),
                t('landing.pricing.p1.f2'),
                t('landing.pricing.p1.f3'),
                t('landing.pricing.p1.f4')
            ],
            icon: <Shield size={24} className="text-blue-400" />,
            color: 'blue'
        },
        {
            id: 'TRADER',
            name: t('landing.pricing.p2.title'),
            price: 27,
            period: t('landing.pricing.period'),
            oldPrice: '$81 / 3 mo.',
            save: 'SAVE 5%',
            popular: true,
            features: [
                t('landing.pricing.p2.f1'),
                t('landing.pricing.p2.f2'),
                t('landing.pricing.p2.f3'),
                t('landing.pricing.p2.f4')
            ],
            icon: <Rocket size={24} className="text-emerald-400" />,
            color: 'emerald'
        },
        {
            id: 'PRO',
            name: t('landing.pricing.p3.title'),
            price: 25,
            period: t('landing.pricing.period'),
            oldPrice: '$150 / 6 mo.',
            save: 'SAVE 10%',
            features: [
                t('landing.pricing.p3.f1'),
                t('landing.pricing.p3.f2'),
                t('landing.pricing.p3.f3'),
                t('landing.pricing.p3.f4')
            ],
            icon: <Crown size={24} className="text-yellow-400" />,
            color: 'yellow'
        },
        {
            id: 'INVESTOR',
            name: t('landing.pricing.p4.title'),
            price: 23,
            period: t('landing.pricing.period'),
            oldPrice: '$276 / year',
            save: 'SAVE 20%',
            features: [
                t('landing.pricing.p4.f1'),
                t('landing.pricing.p4.f2'),
                t('landing.pricing.p4.f3'),
                t('landing.pricing.p4.f4')
            ],
            icon: <Star size={24} className="text-purple-400" />,
            color: 'purple'
        },
    ];

    // Обработчик покупки
    const handleSubscribe = async () => {
        setLoading(true);
        try {
            // 1. Находим цену выбранного плана
            const planObj = PLANS.find(p => p.id === selectedPlan);
            const price = planObj ? planObj.price : 29;

            // 2. Создаем инвойс на сервере, передавая МЕТОД ОПЛАТЫ
            const response = await api.post('/payments/create', {
                plan: selectedPlan,
                price: price,
                method: paymentMethod // 'CARD' или 'CRYPTO'
            });

            // 3. Если сервер вернул ссылку на оплату, переходим по ней
            if (response.data.invoiceUrl) {
                window.location.href = response.data.invoiceUrl;
            } else {
                alert("Error: Could not generate payment invoice.");
            }

        } catch (e: any) {
            alert('Payment Error: ' + (e.response?.data?.message || e.message));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-700 pb-20 max-w-7xl mx-auto">

            {/* HEADER */}
            <div className="mb-8 text-center">
                <h2 className="text-3xl font-black text-white flex items-center justify-center gap-3 uppercase italic tracking-tight">
                    <CreditCard className="text-purple-500" /> {t('landing.nav.pricing')}
                </h2>
                <p className="text-slate-400 mt-2">{t('landing.pricing.subtitle')}</p>
            </div>

            {/* ПЕРЕКЛЮЧАТЕЛЬ МЕТОДА ОПЛАТЫ */}
            <div className="flex justify-center mb-10">
                <div className="bg-slate-800 p-1.5 rounded-xl flex gap-2 border border-slate-700 shadow-xl">
                    <button
                        onClick={() => setPaymentMethod('CARD')}
                        className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-bold transition-all ${paymentMethod === 'CARD'
                                ? 'bg-blue-600 text-white shadow-lg'
                                : 'text-slate-400 hover:text-white'
                            }`}
                    >
                        <CreditCard size={18} /> Credit Card
                    </button>
                    <button
                        onClick={() => setPaymentMethod('CRYPTO')}
                        className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-bold transition-all ${paymentMethod === 'CRYPTO'
                                ? 'bg-[#FCD535] text-black shadow-lg' // Желтый цвет крипты
                                : 'text-slate-400 hover:text-white'
                            }`}
                    >
                        <Wallet size={18} /> Crypto
                    </button>
                </div>
            </div>

            {/* ТЕКУЩИЙ СТАТУС (ЕСЛИ ЕСТЬ ПОДПИСКА) */}
            {!isFree && (
                <div className="mb-12 bg-gradient-to-r from-blue-900/40 to-indigo-900/40 border border-blue-500/30 p-6 rounded-3xl flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <div className="p-4 bg-blue-500/20 rounded-2xl text-blue-400">
                            <Crown size={32} />
                        </div>
                        <div>
                            <p className="text-slate-400 text-sm uppercase font-bold tracking-wider">Current Plan</p>
                            <h3 className="text-2xl font-black text-white">{currentPlan}</h3>
                        </div>
                    </div>
                    <div className="text-right">
                        <p className="text-slate-400 text-sm">Status: <span className="text-green-400 font-bold">ACTIVE</span></p>
                        <p className="text-xs text-slate-500">To change, select a new plan below</p>
                    </div>
                </div>
            )}

            {/* КАРТОЧКИ ТАРИФОВ */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {PLANS.map(plan => {
                    const isSelected = selectedPlan === plan.id;
                    const isCurrent = currentPlan === plan.id;

                    // Логика текста кнопки
                    let buttonText = t('landing.pricing.select'); // "Выбрать"
                    if (loading && isSelected) {
                        buttonText = 'Processing...';
                    } else if (isCurrent) {
                        buttonText = 'Current Plan';
                    } else if (isSelected) {
                        // Если план выбран, но он не текущий -> это смена тарифа
                        buttonText = isFree ? t('landing.pricing.select') : 'Switch Plan';
                    }

                    return (
                        <div
                            key={plan.id}
                            onClick={() => setSelectedPlan(plan.id)}
                            className={`relative p-6 rounded-3xl border-2 transition-all duration-300 cursor-pointer flex flex-col h-full
                    ${isSelected
                                    ? `bg-slate-800 border-${plan.color}-500 shadow-2xl scale-105 z-10`
                                    : 'bg-[#1e293b] border-slate-800 hover:border-slate-600'
                                }
                `}
                        >
                            {/* Сейл / Популярное */}
                            {(plan.popular || plan.save) && (
                                <div className={`absolute -top-3 left-1/2 -translate-x-1/2 text-white text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-wider shadow-lg ${plan.popular ? 'bg-blue-600' : 'bg-green-600'}`}>
                                    {plan.popular ? t('landing.pricing.p2.desc') : plan.save}
                                </div>
                            )}

                            <div className="mb-4">
                                <h4 className={`text-xl font-bold ${isSelected ? 'text-white' : 'text-slate-200'}`}>{plan.name}</h4>
                            </div>

                            <div className="mb-6">
                                <div className="flex items-end gap-1">
                                    <span className="text-4xl font-black text-white">${plan.price}</span>
                                    <span className="text-slate-500 mb-1 font-medium">{plan.period}</span>
                                </div>
                                {plan.oldPrice && <p className="text-xs text-slate-500 mt-1 font-mono">{plan.oldPrice}</p>}
                            </div>

                            <ul className="space-y-3 mb-8 flex-1">
                                {plan.features.map((feature, idx) => (
                                    <li key={idx} className="flex items-start gap-3 text-sm text-slate-300">
                                        <CheckCircle size={16} className={`shrink-0 mt-0.5 text-${plan.color}-400`} />
                                        {feature}
                                    </li>
                                ))}
                            </ul>

                            <button
                                onClick={(e) => { e.stopPropagation(); handleSubscribe(); }}
                                disabled={loading || isCurrent}
                                className={`w-full py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2
                        ${isSelected
                                        ? `bg-${plan.color}-600 hover:bg-${plan.color}-500 text-white shadow-lg`
                                        : 'bg-slate-900 text-slate-400 hover:bg-slate-800 border border-slate-700'
                                    } ${isCurrent ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                {/* Иконка меняется в зависимости от метода */}
                                {paymentMethod === 'CARD' && !loading && !isCurrent && <CreditCard size={16} />}
                                {paymentMethod === 'CRYPTO' && !loading && !isCurrent && <Wallet size={16} />}
                                {buttonText}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};